import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Camera, Search, Download, UserCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import QrScanner from "../../components/shared/QrScanner";
import { useAuth } from "../../context/AuthContext";
import ScanResultOverlay from "./ScanResultOverlay";

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-700",
  used: "bg-slate-200 text-slate-600",
  refunded: "bg-red-100 text-red-600",
  cancelled: "bg-amber-100 text-amber-700",
  consolidated: "bg-blue-100 text-blue-700",
};

export default function AccountantTickets({ eventId, onBack }) {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  // Scanner state machine: null → scanner closed. "scanning" → camera open.
  // Otherwise it holds the validate response so we can show the valid/invalid
  // marker and the Mark Attendance action.
  const [scanState, setScanState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [manual, setManual] = useState(null);
  const [selected, setSelected] = useState([]);
  const markManual = async () => {
    try {
      if (manual.length === 1) {
        await authAxios.post(`/ticketing/accountant/tickets/${manual[0].id}/manual-attend`);
        toast.success("Attendance marked");
      } else {
        const r = await authAxios.post(`/ticketing/accountant/events/${eventId}/manual-attend-bulk`, { ticket_ids: manual.map(t => t.id) });
        if (r.data.skipped.length) toast.warning(`${r.data.marked} marked, ${r.data.skipped.length} skipped (${r.data.skipped[0].reason})`);
        else toast.success(`${r.data.marked} tickets marked attended`);
      }
      setManual(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark attendance"); }
  };
  const { authAxios } = useAuth();

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (tierFilter) params.set("ticket_type_id", tierFilter);
      if (search.trim()) params.set("search", search.trim());
      const q = params.toString() ? `?${params.toString()}` : "";
      const r = await authAxios.get(`/ticketing/accountant/events/${eventId}/tickets${q}`);
      setData(r.data);
      setSelected([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load tickets");
    }
  };

  useEffect(() => { load(); }, [eventId, statusFilter, tierFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQrDecoded = async (qrCode) => {
    // Called by <QrScanner> the moment a code is decoded. We flip immediately
    // to "validating" so the camera closes; then show the valid/invalid card.
    setScanState({ phase: "validating" });
    try {
      const r = await authAxios.post("/ticketing/scan/validate", { qr_code: qrCode });
      setScanState({ phase: "result", ...r.data });
    } catch (e) {
      setScanState({ phase: "result", valid: false,
        message: e.response?.data?.detail || "Scan failed",
      });
    }
  };

  const markAttendance = async () => {
    if (!scanState?.ticket_id) return;
    setConfirming(true);
    try {
      const res = await authAxios.post("/ticketing/scan/confirm", { ticket_id: scanState.ticket_id });
      const credit = res.data?.credit;
      const who = scanState.attendee_name || scanState.buyer_name || "guest";
      if (credit?.issued) {
        const dest = credit.destination === "wallet"
          ? "wallet"
          : `coupon (code ${credit.coupon_code})`;
        toast.success(`Attendance marked for ${who} · €${credit.amount.toFixed(2)} credited to ${dest}`);
      } else {
        toast.success(`Attendance marked for ${who}`);
      }
      setScanState({ phase: "scanning" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to mark attendance");
    } finally {
      setConfirming(false);
    }
  };

  const s = data?.summary || {};

  return (
    <div data-testid="accountant-tickets">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4">
        <ArrowLeft className="w-5 h-5" /> Back to Event
      </button>

      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] rounded-2xl p-3 sm:p-4 mb-4 text-white flex items-center justify-between gap-3">
        <h3 className="font-bold text-sm sm:text-base">Scan Ticket QR</h3>
        <button
          onClick={() => setScanState({ phase: "scanning" })}
          className="px-3 py-2 rounded-xl font-medium flex items-center gap-1 bg-white text-[#1e3a5f] text-sm shrink-0"
          data-testid="ticket-scan-toggle"
        >
          <Camera className="w-5 h-5" /> Start Scan
        </button>
      </div>

      {scanState?.phase === "scanning" && (
        <QrScanner
          onScan={handleQrDecoded}
          onClose={() => setScanState(null)}
          onScanAnother={() => setScanState({ phase: "scanning" })}
        />
      )}

      {scanState?.phase === "validating" && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center" data-testid="scan-validating">
          <div className="bg-white rounded-2xl p-6 text-center">
            <p className="text-sm text-slate-600 animate-pulse">Validating…</p>
          </div>
        </div>
      )}

      {scanState?.phase === "result" && (
        <ScanResultOverlay
          result={scanState}
          confirming={confirming}
          onMark={markAttendance}
          onClose={() => setScanState(null)}
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <StatBox label="Total" value={s.total_count ?? "—"} />
        <StatBox label="Paid" value={s.paid_count ?? "—"} color="green" />
        <StatBox label="Used" value={s.used_count ?? "—"} color="slate" />
        <StatBox label="Refunded" value={s.refunded_count ?? "—"} color="red" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <div className="flex gap-2 w-full sm:w-auto">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 rounded-xl border text-sm"
          data-testid="acc-ticket-status-filter"
        >
          <option value="">All statuses</option>
          <option value="paid">Paid (not checked in)</option>
          <option value="used">Used (checked in)</option>
          <option value="refunded">Refunded</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={tierFilter}
          onChange={e => setTierFilter(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 rounded-xl border text-sm"
          data-testid="acc-ticket-tier-filter"
        >
          <option value="">All ticket types</option>
          {(data?.tiers || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") load(); }}
          placeholder="Search buyer / tier / ID..."
          className="flex-1 min-w-[140px] px-3 py-2 rounded-xl border text-sm"
          data-testid="acc-ticket-search-input"
        />
        <button
          onClick={load}
          className="px-3 py-2 rounded-xl bg-[#1e3a5f] text-white text-sm font-medium flex items-center gap-1 shrink-0"
          data-testid="acc-ticket-search-btn"
        >
          <Search className="w-4 h-4" /> <span className="hidden sm:inline">Search</span>
        </button>
        <button
          onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (statusFilter) params.set("status", statusFilter);
              if (tierFilter) params.set("ticket_type_id", tierFilter);
              if (search.trim()) params.set("search", search.trim());
              const q = params.toString() ? `?${params.toString()}` : "";
              const r = await authAxios.get(`/ticketing/accountant/events/${eventId}/tickets/export${q}`, { responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url; a.download = `tickets_${eventId.slice(0,8)}.xlsx`;
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast.success("Excel downloaded");
            } catch { toast.error("Export failed"); }
          }}
          className="px-3 py-2 rounded-xl bg-[#2d8a4e] text-white text-sm font-medium flex items-center gap-1 shrink-0"
          data-testid="acc-ticket-export-btn"
        ><Download className="w-4 h-4" /> <span className="hidden sm:inline">Excel</span></button>
      </div>

      <AlertDialog open={!!manual} onOpenChange={(o) => !o && setManual(null)}>
        <AlertDialogContent data-testid="acc-manual-attend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{manual?.length > 1 ? `Mark ${manual.length} tickets as attended?` : "Mark as attended?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {manual?.length === 1 && `${manual[0].attendee_name || manual[0].buyer_name || "Guest"} · ${manual[0].tier_name}${manual[0].variant_name ? ` · ${manual[0].variant_name}` : ""}. `}
              Use this only when the scan was missed. No on-attend credit is issued for manual marks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="acc-manual-attend-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={markManual} className="bg-green-600 hover:bg-green-700" data-testid="acc-manual-attend-confirm">{manual?.length > 1 ? `Mark ${manual.length} attended` : "Mark attended"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkBar tickets={data?.tickets || []} selected={selected} setSelected={setSelected} onMark={setManual} />

      <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
        <div className="divide-y divide-slate-100">
          {(data?.tickets || []).map(t => (
            <div key={t.id} className="p-3 flex items-center gap-3" data-testid={`acc-ticket-row-${t.id}`}>
              {t.can_mark_attended && (
                <input type="checkbox" checked={selected.includes(t.id)} aria-label="Select ticket"
                  onChange={e => setSelected(e.target.checked ? [...selected, t.id] : selected.filter(x => x !== t.id))}
                  className="w-4 h-4 accent-green-600 shrink-0" data-testid={`acc-ticket-select-${t.id}`} />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[#1e3a5f] truncate">
                  {t.attendee_name || t.buyer_name || "—"}
                  {t.attendee_name && t.attendee_name !== t.buyer_name && (
                    <span className="ml-1 text-[10px] text-slate-500">(buyer: {t.buyer_name})</span>
                  )}
                </p>
                <p className="text-xs text-slate-500 truncate">{t.tier_name}{t.variant_name ? ` · ${t.variant_name}` : ""} · ID {t.id.slice(0, 8)}</p>
              </div>
              {t.can_mark_attended && (
                <button onClick={() => setManual([t])} className="text-xs font-semibold px-2.5 py-1.5 rounded-full border border-green-600 text-green-700 hover:bg-green-50 shrink-0 flex items-center gap-1" data-testid={`acc-ticket-mark-${t.id}`}>
                  <UserCheck className="w-3.5 h-3.5" /> Mark attended
                </button>
              )}
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[t.status] || "bg-slate-100 text-slate-500"}`}>{t.manual_attendance ? "used · manual" : t.status}</span>
            </div>
          ))}
          {(!data || data.tickets.length === 0) && (
            <div className="p-8 text-center text-slate-400 text-sm">No tickets</div>
          )}
        </div>
      </div>
    </div>
  );
}

function BulkBar({ tickets, selected, setSelected, onMark }) {
  const eligible = tickets.filter(t => t.can_mark_attended);
  if (eligible.length < 2) return null;
  const all = selected.length === eligible.length;
  return (
    <div className="sticky top-0 z-10 mb-2 bg-white/95 backdrop-blur rounded-2xl shadow-sm px-3 py-2 flex items-center gap-3" data-testid="acc-bulk-bar">
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer flex-1 min-w-0">
        <input type="checkbox" checked={all} onChange={() => setSelected(all ? [] : eligible.map(t => t.id))} className="w-4 h-4 accent-green-600" data-testid="acc-bulk-select-all" />
        <span className="truncate">{selected.length ? `${selected.length} selected` : `Select all missed (${eligible.length})`}</span>
      </label>
      <button disabled={!selected.length} onClick={() => onMark(tickets.filter(t => selected.includes(t.id)))} data-testid="acc-bulk-mark-btn"
        className="px-3 py-1.5 rounded-full bg-green-600 text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-40 shrink-0">
        <UserCheck className="w-3.5 h-3.5" /> Mark {selected.length || ""} attended
      </button>
    </div>
  );
}

function StatBox({ label, value, color = "slate" }) {
  const colors = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-green-50 text-green-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div className={`p-3 rounded-2xl ${colors[color]}`}>
      <p className="text-[10px] uppercase tracking-wider opacity-60">{label}</p>
      <p className="text-xl font-extrabold mt-1">{value}</p>
    </div>
  );
}
